import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Alert,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { Close, CheckCircle, Receipt, Add, Delete, LocalOffer, ExpandMore, ExpandLess } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { PaymentMethod } from '../types/entities';

interface PaymentFormData {
  method: PaymentMethod;
  amount: number;
}

interface DiscountFormData {
  type: 'percent' | 'fixed';
  amount: number;
}

interface ServiceDiscountFormData {
  serviceIndex: number;
  type: 'percent' | 'fixed';
  amount: number;
}

interface FinalizeAppointmentModalProps {
  open: boolean;
  onClose: () => void;
  onFinalize: (payments: PaymentFormData[], discount?: DiscountFormData, serviceDiscounts?: ServiceDiscountFormData[]) => void;
  appointment: {
    id: number;
    client: { name: string };
    hairdresser: { name: string };
    date: string;
    services: Array<{
      name: string;
      price: number;
      duration: number;
    }>;
    totalPrice: number;
    totalDuration: number;
  };
}

const FinalizeAppointmentModal: React.FC<FinalizeAppointmentModalProps> = ({
  open,
  onClose,
  onFinalize,
  appointment,
}) => {
  const { t } = useTranslation();
  const [isProcessing, setIsProcessing] = useState(false);
  const [payments, setPayments] = useState<PaymentFormData[]>([
    { method: PaymentMethod.CASH, amount: 0 }
  ]);
  const [hasDiscount, setHasDiscount] = useState(false);
  const [discount, setDiscount] = useState<DiscountFormData>({
    type: 'percent',
    amount: 0
  });
  const [serviceDiscounts, setServiceDiscounts] = useState<ServiceDiscountFormData[]>([]);
  const [isDiscountSectionExpanded, setIsDiscountSectionExpanded] = useState(false);

  const handleFinalize = async () => {
    setIsProcessing(true);
    try {
      await onFinalize(payments, hasDiscount ? discount : undefined, serviceDiscounts.length > 0 ? serviceDiscounts : undefined);
      onClose();
    } catch (error) {
      console.error('Error finalizing appointment:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pl-PL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDuration = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    
    if (hours === 0) {
      return `${mins} ${mins !== 1 ? t('appointments.minutes') : t('appointments.minute')}`;
    } else if (mins === 0) {
      return `${hours} ${hours !== 1 ? t('appointments.hours') : t('appointments.hour')}`;
    } else {
      return `${hours} ${hours !== 1 ? t('appointments.hours') : t('appointments.hour')} ${mins} ${mins !== 1 ? t('appointments.minutes') : t('appointments.minute')}`;
    }
  };

  const totalPaid = payments.reduce((sum: number, payment: PaymentFormData) => sum + payment.amount, 0);
  
  // Calculate service-level discounts
  let totalServiceDiscountAmount = 0;
  serviceDiscounts.forEach(serviceDiscount => {
    const service = appointment.services[serviceDiscount.serviceIndex];
    if (service) {
      if (serviceDiscount.type === 'percent') {
        totalServiceDiscountAmount += (service.price * serviceDiscount.amount) / 100;
      } else {
        totalServiceDiscountAmount += Math.min(serviceDiscount.amount, service.price);
      }
    }
  });
  
  // Calculate appointment-level discount amount
  let appointmentDiscountAmount = 0;
  if (hasDiscount && discount.amount > 0) {
    if (discount.type === 'percent') {
      appointmentDiscountAmount = (appointment.totalPrice * discount.amount) / 100;
    } else {
      appointmentDiscountAmount = Math.min(discount.amount, appointment.totalPrice);
    }
  }
  
  const finalPrice = appointment.totalPrice - totalServiceDiscountAmount - appointmentDiscountAmount;
  const remainingToPay = finalPrice - totalPaid;

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: 2,
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box display="flex" alignItems="center" gap={1}>
          <Receipt color="primary" />
          <Typography variant="h6">{t('appointments.finalizeAppointment')}</Typography>
        </Box>
        <IconButton onClick={onClose} size="small" title={t('common.close')}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ padding: 3 }}>
        {/* Services */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            {t('appointments.servicesProvided')}
          </Typography>
          {appointment.services.map((service, index) => (
            <Box key={index} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 1 }}>
              <Typography variant="subtitle1" gutterBottom>
                {service.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('common.duration')}: {formatDuration(service.duration)} | {t('common.price')}: {service.price} zł
              </Typography>
            </Box>
          ))}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Unified Discount Section */}
        <Box sx={{ mb: 3 }}>
          <Box 
            sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              cursor: 'pointer',
              p: 1,
              borderRadius: 1,
              '&:hover': {
                backgroundColor: 'action.hover',
              }
            }}
            onClick={() => setIsDiscountSectionExpanded(!isDiscountSectionExpanded)}
          >
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocalOffer color="primary" />
              {t('appointments.discountsOptional')}
            </Typography>
            <IconButton size="small">
              {isDiscountSectionExpanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>
          
          {isDiscountSectionExpanded && (
            <Box sx={{ mt: 2 }}>
              {/* Service-Level Discounts */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ color: 'text.secondary' }}>
                  {t('appointments.serviceLevelDiscounts')}
                </Typography>
                
                {appointment.services.map((service, serviceIndex) => {
                  const existingDiscount = serviceDiscounts.find(sd => sd.serviceIndex === serviceIndex);
                  const hasServiceDiscount = !!existingDiscount;
                  
                  return (
                    <Card key={serviceIndex} variant="outlined" sx={{ mb: 2 }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Box>
                            <Typography variant="subtitle1">{service.name}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {t('common.price')}: {service.price} zł
                            </Typography>
                          </Box>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={hasServiceDiscount}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setServiceDiscounts([...serviceDiscounts, {
                                      serviceIndex,
                                      type: 'percent',
                                      amount: 0
                                    }]);
                                  } else {
                                    setServiceDiscounts(serviceDiscounts.filter(sd => sd.serviceIndex !== serviceIndex));
                                  }
                                }}
                              />
                            }
                            label={t('appointments.applyDiscount')}
                          />
                        </Box>
                        
                        {hasServiceDiscount && (
                          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                            <FormControl sx={{ minWidth: 120 }}>
                              <InputLabel>{t('appointments.type')}</InputLabel>
                              <Select
                                value={existingDiscount?.type || 'percent'}
                                label={t('appointments.type')}
                                onChange={(e) => {
                                  const newServiceDiscounts = [...serviceDiscounts];
                                  const index = newServiceDiscounts.findIndex(sd => sd.serviceIndex === serviceIndex);
                                  if (index >= 0) {
                                    newServiceDiscounts[index].type = e.target.value as 'percent' | 'fixed';
                                  }
                                  setServiceDiscounts(newServiceDiscounts);
                                }}
                              >
                                <MenuItem value="percent">{t('appointments.percentage')} (%)</MenuItem>
                                <MenuItem value="fixed">{t('appointments.fixedAmount')} (zł)</MenuItem>
                              </Select>
                            </FormControl>
                            
                            <TextField
                              label={existingDiscount?.type === 'percent' ? t('appointments.percentage') : t('appointments.amount') + ' (zł)'}
                              type="number"
                              value={existingDiscount?.amount || 0}
                              onChange={(e) => {
                                const newServiceDiscounts = [...serviceDiscounts];
                                const index = newServiceDiscounts.findIndex(sd => sd.serviceIndex === serviceIndex);
                                if (index >= 0) {
                                  newServiceDiscounts[index].amount = parseFloat(e.target.value) || 0;
                                }
                                setServiceDiscounts(newServiceDiscounts);
                              }}
                              inputProps={{ 
                                min: 0, 
                                max: existingDiscount?.type === 'percent' ? 100 : undefined,
                                step: existingDiscount?.type === 'percent' ? 1 : 0.01
                              }}
                              sx={{ flex: 1 }}
                            />
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </Box>
      
              {/* Appointment-Level Discount */}
              <Box>
                <Typography variant="subtitle1" gutterBottom sx={{ color: 'text.secondary' }}>
                  {t('appointments.appointmentLevelDiscount')}
                </Typography>
                
                <Card variant="outlined">
                  <CardContent>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={hasDiscount}
                          onChange={(e) => setHasDiscount(e.target.checked)}
                        />
                      }
                      label={t('appointments.applyDiscountToEntireAppointment')}
                      sx={{ mb: 2 }}
                    />
                    
                    {hasDiscount && (
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <FormControl sx={{ minWidth: 120 }}>
                          <InputLabel>{t('appointments.type')}</InputLabel>
                          <Select
                            value={discount.type}
                            label={t('appointments.type')}
                            onChange={(e) => setDiscount({ ...discount, type: e.target.value as 'percent' | 'fixed' })}
                          >
                            <MenuItem value="percent">{t('appointments.percentage')} (%)</MenuItem>
                            <MenuItem value="fixed">{t('appointments.fixedAmount')} (zł)</MenuItem>
                          </Select>
                        </FormControl>
                        
                        <TextField
                          label={discount.type === 'percent' ? t('appointments.percentage') : t('appointments.amount') + ' (zł)'}
                          type="number"
                          value={discount.amount}
                          onChange={(e) => setDiscount({ ...discount, amount: parseFloat(e.target.value) || 0 })}
                          inputProps={{ 
                            min: 0, 
                            max: discount.type === 'percent' ? 100 : undefined,
                            step: discount.type === 'percent' ? 1 : 0.01
                          }}
                          sx={{ flex: 1 }}
                        />
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Box>
            </Box>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Payment Processing */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            {t('appointments.paymentProcessing')}
          </Typography>
          
          {payments.map((payment: PaymentFormData, index: number) => (
            <Box key={index} sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
              <FormControl fullWidth>
                <InputLabel>{t('appointments.paymentMethod')}</InputLabel>
                <Select
                  value={payment.method}
                  label={t('appointments.paymentMethod')}
                  onChange={(e) => {
                    const newPayments = [...payments];
                    newPayments[index].method = e.target.value as PaymentMethod;
                    setPayments(newPayments);
                  }}
                >
                  <MenuItem value={PaymentMethod.CASH}>{t('appointments.cash')}</MenuItem>
                  <MenuItem value={PaymentMethod.CARD}>{t('appointments.card')}</MenuItem>
                  <MenuItem value={PaymentMethod.VOUCHER}>{t('appointments.voucher')}</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label={t('appointments.amount') + ' (zł)'}
                type="number"
                value={payment.amount}
                onChange={(e) => {
                  const newPayments = [...payments];
                  newPayments[index].amount = parseFloat(e.target.value) || 0;
                  setPayments(newPayments);
                }}
                fullWidth
                inputProps={{ step: "0.01", min: "0" }}
              />
              {payments.length > 1 && (
                <IconButton 
                  onClick={() => setPayments(payments.filter((_: PaymentFormData, i: number) => i !== index))}
                  color="error"
                >
                  <Delete />
                </IconButton>
              )}
            </Box>
          ))}

          <Button
            onClick={() => setPayments([...payments, { method: PaymentMethod.CASH, amount: 0 }])}
            startIcon={<Add />}
            variant="outlined"
            sx={{ mb: 2 }}
          >
            {t('appointments.addAnotherPayment')}
          </Button>

          {/* Payment Summary */}
          <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="body1">
                <strong>{t('appointments.totalPrice')}:</strong>
              </Typography>
              <Typography variant="body1">
                {appointment.totalPrice} zł
              </Typography>
            </Box>
            
            {totalServiceDiscountAmount > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="body1" color="success.main">
                  <strong>{t('appointments.serviceDiscounts')}:</strong>
                </Typography>
                <Typography variant="body1" color="success.main">
                  -{totalServiceDiscountAmount.toFixed(2)} zł
                </Typography>
              </Box>
            )}
            
            {hasDiscount && appointmentDiscountAmount > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="body1" color="success.main">
                  <strong>{t('appointments.appointmentDiscount')} ({discount.type === 'percent' ? `${discount.amount}%` : t('appointments.fixed')}):</strong>
                </Typography>
                <Typography variant="body1" color="success.main">
                  -{appointmentDiscountAmount.toFixed(2)} zł
                </Typography>
              </Box>
            )}
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, pt: 1, borderTop: '1px solid #e0e0e0' }}>
              <Typography variant="body1">
                <strong>{t('appointments.finalPrice')}:</strong>
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                {finalPrice.toFixed(2)} zł
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="body1">
                <strong>{t('appointments.totalPaid')}:</strong>
              </Typography>
              <Typography variant="body1" color={totalPaid === finalPrice ? 'success.main' : 'warning.main'}>
                {totalPaid.toFixed(2)} zł
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body1">
                <strong>{t('appointments.remaining')}:</strong>
              </Typography>
              <Typography variant="body1" color={remainingToPay === 0 ? 'success.main' : 'error.main'}>
                {remainingToPay.toFixed(2)} zł
              </Typography>
            </Box>
          </Box>

          {remainingToPay !== 0 && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {t('appointments.totalPaidMustEqualFinalPrice', { finalPrice: finalPrice.toFixed(2) })}
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ padding: 3, gap: 2 }}>
        <Button 
          onClick={onClose}
          variant="outlined"
          disabled={isProcessing}
        >
          {t('common.cancel')}
        </Button>
        <Button 
          onClick={handleFinalize}
          variant="contained"
          color="success"
          disabled={isProcessing || remainingToPay !== 0 || payments.some((p: PaymentFormData) => p.amount <= 0) || (hasDiscount && discount.amount <= 0) || serviceDiscounts.some(sd => sd.amount <= 0)}
          startIcon={<CheckCircle />}
        >
          {isProcessing ? t('appointments.processing') : t('appointments.finalizeAppointment')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FinalizeAppointmentModal;
